// Shared "armed confirm" hook for the Twilio settings modal. A destructive
// control's first click arms it (id stored in armedId); a second click on the
// same armed control performs the action. Arming auto-resets after a timeout,
// on Escape, or when the caller disarms (e.g. mouse-leave). This replaces
// window.confirm/alert/prompt, which are banned by CLAUDE.md.

import { useCallback, useEffect, useRef, useState } from 'react';

export default function useArmedConfirm(timeoutMs = 4000) {
  const [armedId, setArmedId] = useState(null);
  const timerRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const disarm = useCallback(() => {
    clearTimer();
    setArmedId(null);
  }, [clearTimer]);

  const arm = useCallback(
    (id) => {
      clearTimer();
      setArmedId(id);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        setArmedId(null);
      }, timeoutMs);
    },
    [clearTimer, timeoutMs]
  );

  useEffect(() => clearTimer, [clearTimer]);

  // While something is armed, Escape disarms it (also: the × button, the
  // auto-reset timeout, and mouse-leave handlers on the calling components).
  useEffect(() => {
    if (armedId == null) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') disarm();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [armedId, disarm]);

  return { armedId, arm, disarm };
}

// Open/close state for a popover dropdown. Closes on Escape and on a mousedown
// outside the popover. Attach `ref` to the wrapper that contains the trigger,
// and `panelRef` to the panel. The panel may be portaled out of the wrapper's
// DOM subtree (see SearchableDropdown), so the outside-click check tests both
// refs — a click inside either the trigger wrapper or the panel is "inside".
export function useDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const panelRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      const inTrigger = ref.current && ref.current.contains(e.target);
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inTrigger && !inPanel) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);
  return { open, setOpen, ref, panelRef };
}
